
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useCommunity } from '@/contexts/CommunityContext';

export interface PreviewUser {
  id: string;
  firstname: string | null;
  lastname: string | null;
  email: string | null;
}

export interface PreviewBusiness {
  id: string;
  name: string;
  user_id: string;
  contact_email: string;
  user_profiles: {
    firstname: string | null;
    lastname: string | null;
    email: string | null;
  };
  description?: string;
  category_id?: string;
  contact_phone?: string;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  county?: string;
  postal_code?: string;
  full_address?: string; // Computed field
  website?: string;
  facebook_url?: string;
  instagram_url?: string;
  tiktok_url?: string;
  google_maps_url?: string;
  application_comments?: string;
  created_at?: string;
}

export function usePreviewUsers(enabled: boolean = true) {
  const { currentCommunity } = useCommunity();

  return useQuery({
    queryKey: ['preview-users', currentCommunity?.id],
    enabled: enabled && !!currentCommunity?.id,
    queryFn: async () => {
      if (!currentCommunity?.id) return [];

      const { data, error } = await supabase
        .from('community_members')
        .select(`
          user_id,
          user_profiles!inner(
            id,
            firstname,
            lastname,
            email
          )
        `)
        .eq('community_id', currentCommunity.id)
        .eq('status', 'ACTIVE')
        .not('user_profiles.firstname', 'is', null)
        .not('user_profiles.lastname', 'is', null)
        .limit(10);

      if (error) throw error;

      return data?.map(member => ({
        id: member.user_profiles.id,
        firstname: member.user_profiles.firstname,
        lastname: member.user_profiles.lastname,
        email: member.user_profiles.email,
      })) as PreviewUser[];
    },
  });
}

export function usePreviewBusinesses(enabled: boolean = true) {
  const { currentCommunity } = useCommunity();

  return useQuery({
    queryKey: ['preview-businesses', currentCommunity?.id],
    enabled: enabled && !!currentCommunity?.id,
    queryFn: async () => {
      if (!currentCommunity?.id) return [];

      const { data, error } = await supabase
        .from('businesses')
        .select(`
          id,
          name,
          user_id,
          contact_email,
          description,
          category_id,
          contact_phone,
          address_line1,
          address_line2,
          city,
          county,
          postal_code,
          website,
          facebook_url,
          instagram_url,
          tiktok_url,
          google_maps_url,
          application_comments,
          created_at,
          user_profiles!fk_businesses_user_profiles (
            firstname,
            lastname,
            email
          )
        `)
        .eq('community_id', currentCommunity.id)
        .eq('is_active', true)
        .not('user_profiles.firstname', 'is', null)
        .not('user_profiles.lastname', 'is', null)
        .limit(10);

      if (error) throw error;

      return data?.map(business => {
        // Compute full_address from individual address fields
        const addressParts = [
          business.address_line1,
          business.address_line2,
          business.city,
          business.county,
          business.postal_code
        ].filter(Boolean);
        const full_address = addressParts.length > 0 ? addressParts.join(', ') : undefined;

        return {
          id: business.id,
          name: business.name,
          user_id: business.user_id,
          contact_email: business.contact_email,
          user_profiles: {
            firstname: business.user_profiles.firstname,
            lastname: business.user_profiles.lastname,
            email: business.user_profiles.email,
          },
          description: business.description,
          category_id: business.category_id,
          contact_phone: business.contact_phone,
          address_line1: business.address_line1,
          address_line2: business.address_line2,
          city: business.city,
          county: business.county,
          postal_code: business.postal_code,
          full_address,
          website: business.website,
          facebook_url: business.facebook_url,
          instagram_url: business.instagram_url,
          tiktok_url: business.tiktok_url,
          google_maps_url: business.google_maps_url,
          application_comments: business.application_comments,
          created_at: business.created_at,
        };
      }) as PreviewBusiness[];
    },
  });
}
